/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataSourceTable, Definition, QueryOptions, QueryResults, RecordTypes } from '@mode/shared/data-access-algolia';
import { DatasourceDomain, OrgTypes, Table } from '@mode/shared/contract-common';
import columnHelpers from './column-suggestion-helper';
import { createTableIconClass } from './icon-provider';
import helper from './helper';
import { SuggestionService } from './suggestion.service';

export function getTableCompleters(
  suggestionService: SuggestionService,
  datasource: DatasourceDomain.Datasource,
  owner: OrgTypes.Organization,
  includeTables = true,
  includeDefinitions = true
): AceAjax.Completer {
  return {
    getCompletions(
      editor: AceAjax.Editor,
      session: AceAjax.IEditSession,
      cursorPosition: AceAjax.Position,
      prefix: string,
      callback: AceAjax.CompletionCallback
    ) {
      const queryText = helper.getQueryText(session, cursorPosition, prefix);
      const isCompletingDefinition = helper.endsWithDefinitionClause(queryText);
      const hideIfCommenting = helper.isCursorWithinComment(session, cursorPosition);
      const hasDefinitionSyntax = queryText.endsWith('@') || prefix.startsWith('@');

      const hideSuggestion =
        prefix.length === 0 ||
        hideIfCommenting ||
        (!includeTables && !includeDefinitions) ||
        !(
          helper.endsWithDefinitionClause(queryText) ||
          helper.isTableCompletionNeedeed(queryText) ||
          hasDefinitionSyntax
        );

      if (hideSuggestion) {
        callback(null, []);
        return;
      }

      if (!datasource) {
        return;
      }
      const tableFilter = datasource.public ? undefined : `dataSource_${datasource.token}`;

      let opts: QueryOptions<any>;

      if (includeTables && includeDefinitions) {
        opts = searchIndexerOptionsForTablesAndDefinitions(prefix, datasource, tableFilter, owner);
      } else if (includeTables) {
        opts = searchIndexerOptionsForTables(prefix, datasource, tableFilter);
      } else if (includeDefinitions) {
        opts = searchIndexerOptionsForDefinitions(prefix, datasource, owner);
      }

      suggestionService
        .getTables(datasource, opts, isCompletingDefinition)
        .then(({ data: suggestedTables }) => callback(null, suggestedTables))
        .catch(function (e) {
          console.error('search error', e.toString());
          callback(null, []);
        });
    },
    identifierRegexps: [/[a-zA-Z_0-9.\@\$\-\u00A2-\uFFFF]/],
  };
}

function searchIndexerOptionsForTables(prefix: string, datasource: DatasourceDomain.Datasource, tableFilter: string) {
  const opts: QueryOptions<'table' | 'dataSourceTable'> = {
    query: prefix.replace('@', ''),
    indexes: datasource.public ? ['table'] : ['dataSourceTable'],
    attrsToSearchIn: {
      table: ['name', 'schema', 'fullName'],
      dataSourceTable: ['name', 'schema', 'fullName'],
    },
    perPage: 20,
    filters: {
      table: tableFilter,
      dataSourceTable: tableFilter,
    },
  };
  return opts;
}

function searchIndexerOptionsForDefinitions(
  prefix: string,
  datasource: DatasourceDomain.Datasource,
  owner: OrgTypes.Organization
) {
  const opts: QueryOptions<'definition'> = {
    query: prefix.replace('@', ''),
    indexes: ['definition'],
    attrsToSearchIn: {
      definition: ['name'],
    },
    perPage: 20,
    filters: {
      definition: `dataSource_${datasource.token},${owner.user ? 'user_' : 'organization_'}${owner.token}`,
    },
  };
  return opts;
}

function searchIndexerOptionsForTablesAndDefinitions(
  prefix: string,
  datasource: DatasourceDomain.Datasource,
  tableFilter: string,
  owner: OrgTypes.Organization
) {
  const opts: QueryOptions<'table' | 'dataSourceTable' | 'definition'> = {
    query: prefix.replace('@', ''),
    indexes: datasource.public ? ['table', 'definition'] : ['dataSourceTable', 'definition'],
    attrsToSearchIn: {
      table: ['name', 'schema', 'fullName'],
      dataSourceTable: ['name', 'schema', 'fullName'],
      definition: ['name'],
    },
    perPage: 20,
    filters: {
      table: tableFilter,
      dataSourceTable: tableFilter,
      definition: `dataSource_${datasource.token},${owner.user ? 'user_' : 'organization_'}${owner.token}`,
    },
  };
  return opts;
}

/**
 *
 * @param datasource
 * @param results search results. First element should be tables, second the definitions
 * @param isDefinition flag whether the autocomplete is specifically for a definition or not
 */
export function parseMatchTables(
  datasource: DatasourceDomain.Datasource,
  result: QueryResults<keyof RecordTypes>,
  isDefinition: boolean
): AceAjax.Completion[] {
  let tablesFromAlgolia: (Table | DataSourceTable | Definition)[] =
    (datasource.public ? result.table : result.dataSourceTable) ?? [];

  // minor optimization would be to use the alphabetical indexes and do an inplace insertion sort
  if (isDefinition) {
    // if it's a definition autocomplete, only need to look for definition results
    tablesFromAlgolia = result.definition;
  } else {
    tablesFromAlgolia = result.definition ? [...result.definition, ...tablesFromAlgolia] : tablesFromAlgolia;
  }

  return tablesFromAlgolia.map((table, index) => {
    const isTable = 'fullName' in table;
    const identifier = decodeURIComponent(isTable ? table.fullName : table.name);
    const suggestionType: AceAjax.Completion['suggestionType'] =
      table.objectType === 'definition' ? 'definition' : 'table';
    const suggestedTable = {
      name: identifier,
      value: `${identifier}`, // insert space after completion
      replacement:
        table.objectType === 'definition'
          ? `${table.name}`
          : `${decodeURIComponent(isTable ? table.fullName : undefined)}`,
      meta: '',
      suggestionType,
      infoTitle: isTable ? table.fullName : undefined,
      columnsInfo:
        suggestionType === 'table' ? columnHelpers.getColumnInfoForTableTooltip(table as Table | DataSourceTable) : [], // Definitions don't have columns data ATM
      icon: createTableIconClass(suggestionType),
      highlighted: (isTable ? table._highlightResult.fullName : table._highlightResult.name).value,
      completer: {
        insertMatch(editor: AceAjax.Editor, suggestion: AceAjax.Completion) {
          if (suggestion.suggestionType === 'table') {
            helper.getCompleter().insertMatch(editor, suggestion);
          } else {
            // this exists because we are handling definition autocomplete 3+ different ways
            const { replacement } = suggestion;
            const textToBeFiltered: string = editor['completer'].completions.filterText;

            helper.handleDefinitionReplacement(editor, replacement, textToBeFiltered);
          }
        },
      },
    };
    const { score, matchMask } = helper.getCompletionScore(suggestedTable, index, tablesFromAlgolia.length);
    return Object.assign(suggestedTable, { score, matchMask });
  });
}
